import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { CommonInputProps, Labeled } from "react-admin";
import { Editor } from "@tinymce/tinymce-react";
import { ImageContentInput } from "./ImageContentInput";
import { FormHelperText, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box} from "@mui/material";
import { AutocompleteInput } from "src/components/input";

export const WysiwygInput = (props: WysiwygInputProps) => {
  const {
    className,
    value,
    disabled,
    fullWidth,
    helperText,
    label,
    readOnly,
    required,
    base64,
    validate,
    onChange,
    error,
    ...rest
  } = props;

  const editorRef = useRef(null);
  const imageContentInputRef = useRef(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  const setVars = (value) => {
    value = value
      .replaceAll(process.env.REACT_APP_AWS_CLOUDFRONT_URL, "%cloudFrontUrl%")
      .replaceAll(process.env.REACT_APP_BASE_URL, "%baseUrl%");
    return value;
  };

  const unsetVars = (value) => {
    return value
      .replaceAll("%cloudFrontUrl%", process.env.REACT_APP_AWS_CLOUDFRONT_URL)
      .replaceAll("%baseUrl%", process.env.REACT_APP_BASE_URL);
  };

  const parseValue = (value) => {
    let parsedValue = setVars(value);

    return base64 ? btoa(unescape(encodeURIComponent(parsedValue))) : parsedValue;
  };

  const resolveValue = (value) => {
    let resolvedValue = base64 ? decodeURIComponent(escape(atob(value))) : value;

    return unsetVars(resolvedValue);
  };

  const handleUpdate = (value) => {
    const html = parseValue(value);

    onChange(html);
  };
  
  return (
    <Labeled
      isRequired={required}
      label={label}
      color={error ? "error" : undefined}
      fullWidth={fullWidth}
      sx={{
        ".RaLabeled-label": {
          marginLeft: "14px",
        },
      }}
      {...rest}
    >
      <Root className={className}>
        <Editor
          onInit={(evt, editor) => (editorRef.current = editor)}
          value={resolveValue(value)}
          disabled={disabled || readOnly}
          onEditorChange={handleUpdate}
          init={{
            height: 500,
            menubar: false,
            plugins: [
              "advlist autolink lists link charmap print preview anchor",
              "searchreplace visualblocks code fullscreen",
              "insertdatetime media table paste code wordcount",
              "noneditable"
            ],
            toolbar1:
              "undo redo | formatselect fontselect fontsizeselect",
            toolbar2:
              "bold italic forecolor backcolor | link pagelink variable image",
            toolbar3:
              "alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | " +
              "removeformat | code",
            content_style:
              "body { font-family:Helvetica,Arial,sans-serif; font-size:14px } " +
              "sc { background-color: #1976d2; color: white; padding: 5px 15px; cursor: pointer; margin: 3px; border-radius: 3px; font-weight: bold; font-size: 18px;}",
            convert_urls: false,
            allow_html_in_named_anchor: true,
            allow_unsafe_link_target: true,
            allow_conditional_comments: true,
            noneditable_noneditable_class: "nonedit",
            popup_container: document.body,
            skin: "oxide",
            content_css: "default",
            setup: (editor) => {
              editor.ui.registry.addButton("image", {
                icon: "image",
                tooltip: "Insert Image",
                onAction: function (api): void {
                  if (imageContentInputRef.current) {
                    imageContentInputRef.current.open();
                  }
                },
              });

              editor.ui.registry.addMenuButton("variable", {
                icon: "visualblocks",
                fetch: function (callback) {
                  const items: any = [
                    {
                      type: "menuitem",
                      text: "baseUrl",
                      onAction: function () {
                        editor.insertContent("%baseUrl%");
                      },
                    },
                    {
                      type: "menuitem",
                      text: "cloudFrontUrl",
                      onAction: function () {
                        editor.insertContent("%cloudFrontUrl%");
                      },
                    },
                  ];
                  callback(items);
                },
              });

              editor.ui.registry.addButton('pagelink', {
                icon: 'link',
                tooltip: 'Insert Page Link',
                onAction: function () {
                  setLinkDialogOpen(true);
                }
              });
            },
          }}
          tinymceScriptSrc="/js/tinymce/tinymce.min.js"
        />

        <ImageContentInput
          editor={editorRef}
          ref={imageContentInputRef}
        ></ImageContentInput>

        <PageLinkDialog
          open={linkDialogOpen}
          onClose={() => setLinkDialogOpen(false)}
          editor={editorRef}
        />

        <FormHelperText
          sx={{width: "100%", marginTop: "14px", paddingLeft: "14px"}}
          className={
            error
              ? "tinymce-content-input-error"
              : ""
          }
          error={error}
        >
          {helperText}
        </FormHelperText>
      </Root>
    </Labeled>
  );
};

const PREFIX = "TinyMCEContentInput";
const classes = {
  editorContent: `${PREFIX}-editorContent`,
};
const Root = styled("div", {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  [`& .tox-tinymce`]: {
    width: "100%",
    "& .tox-statusbar": {
      display: "none",
    },
    "&--disabled": {
      "&:before": {
        backgroundColor: theme.palette.action.disabledBackground,
        content: '""',
        width: "100%",
        height: "100%",
        position: "absolute",
        zIndex: 1,
      },
    },
  },
}));

WysiwygInput.defaultProps = {
  base64: false,
  disabled: false,
  required: false,
  readOnly: false,
  fullWidth: false,
  value: "",
};

export type WysiwygInputProps = CommonInputProps & {
  disabled?: boolean;
  readOnly?: boolean;
  base64?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  helperText?: string | React.ReactElement | false;
  className?: string;
  value?: string;
  source: string;
  onChange: any;
  error: any;
};

const PageLinkDialog = ({ open, onClose, editor }) => {
  const [selectedPage, setSelectedPage] = useState(null);
  const [title, setTitle] = useState('');

  useEffect(() => {
    setTitle(selectedPage?.title || '');
  }, [selectedPage]);

  const handleInsert = () => {
    if (!editor?.current || !selectedPage) return;
  
    onClose();
    setTimeout(() => {
      const linkHtml = `<a href="%baseUrl%/${selectedPage.slug}">${title}</a>`;
      editor.current.focus();
      editor.current.insertContent(linkHtml);
    }, 50);
  };

  return (
    <Dialog open={open} onClose={onClose}  fullWidth maxWidth="sm">
      <DialogTitle>Select a Page</DialogTitle>
      <DialogContent>
        <Box mt={2} display="flex" flexDirection="column" gap={2}>
          <AutocompleteInput
              resource="admin/pages"
              label="Page"
              name="page"
              required={true}
              onChangeData="object"
              onChange={(value, field) => setSelectedPage(value)}
              getOptionLabel={(option) => `${option?.title || ''}`}
          />
          <TextField
            label="Title"
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          ></TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box padding={2} display="flex" justifyContent="flex-end" gap={2}>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleInsert} disabled={selectedPage === null} variant="contained" color="primary">
            Insert
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};